import * as Sentry from '@sentry/browser';
import { environment } from '../../environments/environment';
import {
  mergeBaggageHeader,
  planningEntityTraceForRequest,
  planningEntityTraceToBaggage,
} from './planning-entity-trace';

const TRACE_ID_RE = /^[0-9a-f]{32}$/;
const SPAN_ID_RE = /^[0-9a-f]{16}$/;

export interface ClientTraceContext {
  traceId: string;
  spanId: string;
  sampled: boolean;
  traceparent: string;
  sentryTrace: string;
  source: 'sentry' | 'local';
}

let localTraceId: string | null = null;
let localSpanId: string | null = null;

/**
 * Active client trace: Sentry's current span/propagation context when the SDK
 * is initialized, otherwise a page-lifetime W3C trace so local/dev (no DSN)
 * still correlates with server logs.
 */
export function getClientTraceContext(): ClientTraceContext {
  return readSentryTraceContext() ?? getOrCreateLocalTraceContext();
}

/** Headers to attach to planning API requests (W3C + Sentry + entity baggage). */
export function getOutgoingTraceHeaders(body?: unknown): Record<string, string> {
  const ctx = getClientTraceContext();
  const headers: Record<string, string> = {
    traceparent: ctx.traceparent,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'sentry-trace': ctx.sentryTrace,
  };
  const sentryBaggage = readSentryBaggageHeader();
  const entity = planningEntityTraceForRequest(body);
  const baggage = mergeBaggageHeader(
    sentryBaggage,
    entity ? planningEntityTraceToBaggage(entity) : {},
  );
  if (baggage) {
    headers.baggage = baggage;
  }
  return headers;
}

export function shouldPropagateTrace(url: string): boolean {
  if (!url || url.startsWith('blob:') || url.startsWith('data:')) {
    return false;
  }
  try {
    const resolved = new URL(url, 'http://planning.local');
    if (resolved.pathname.startsWith('/api')) {
      return true;
    }
    const api = new URL(environment.serverApiUrl, 'http://planning.local');
    return resolved.origin === api.origin;
  } catch {
    return url.includes('/api/');
  }
}

export function toW3cTraceparent(
  traceId: string,
  spanId: string,
  sampled: boolean,
): string {
  return `00-${traceId}-${spanId}-${sampled ? '01' : '00'}`;
}

export function toSentryTrace(traceId: string, spanId: string, sampled: boolean): string {
  return `${traceId}-${spanId}-${sampled ? '1' : '0'}`;
}

/** Test helper: drop the page-lifetime fallback so cases stay independent. */
export function resetLocalTraceContextForTests(): void {
  localTraceId = null;
  localSpanId = null;
}

function readSentryTraceContext(): ClientTraceContext | null {
  try {
    const span = Sentry.getActiveSpan();
    if (span) {
      const json = Sentry.spanToJSON(span);
      const traceId = normalizeTraceId(json.trace_id);
      const spanId = normalizeSpanId(json.span_id);
      if (traceId && spanId) {
        const sentryTrace = Sentry.spanToTraceHeader(span);
        const sampled = sentryTrace.endsWith('-1');
        return buildContext(traceId, spanId, sampled, 'sentry');
      }
    }
    const propagation = Sentry.getCurrentScope()?.getPropagationContext();
    const traceId = normalizeTraceId(propagation?.traceId);
    const spanId = normalizeSpanId(propagation?.spanId) ?? randomHex(8);
    if (traceId) {
      return buildContext(traceId, spanId, propagation?.sampled !== false, 'sentry');
    }
  } catch {
    return null;
  }
  return null;
}

function readSentryBaggageHeader(): string | undefined {
  try {
    const span = Sentry.getActiveSpan();
    if (!span) {
      return undefined;
    }
    return Sentry.spanToBaggageHeader(span) || undefined;
  } catch {
    return undefined;
  }
}

function getOrCreateLocalTraceContext(): ClientTraceContext {
  if (!localTraceId) {
    localTraceId = randomHex(16);
  }
  if (!localSpanId) {
    localSpanId = randomHex(8);
  }
  return buildContext(localTraceId, localSpanId, true, 'local');
}

function buildContext(
  traceId: string,
  spanId: string,
  sampled: boolean,
  source: ClientTraceContext['source'],
): ClientTraceContext {
  return {
    traceId,
    spanId,
    sampled,
    source,
    traceparent: toW3cTraceparent(traceId, spanId, sampled),
    sentryTrace: toSentryTrace(traceId, spanId, sampled),
  };
}

function normalizeTraceId(value: string | undefined): string | null {
  if (!value) {
    return null;
  }
  const hex = value.replace(/-/g, '').toLowerCase();
  if (TRACE_ID_RE.test(hex)) {
    return hex;
  }
  if (/^[0-9a-f]{16}$/.test(hex)) {
    return hex.padStart(32, '0');
  }
  return null;
}

function normalizeSpanId(value: string | undefined): string | null {
  if (!value) {
    return null;
  }
  const hex = value.replace(/-/g, '').toLowerCase();
  return SPAN_ID_RE.test(hex) ? hex : null;
}

function randomHex(byteCount: number): string {
  const bytes = new Uint8Array(byteCount);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
